// The forms of the client application (SEP-293).
//
// The firm opens seven account types, and each type has its own paper
// application form. One form serves the individual account types (General
// Investment, ISA and Joint). The other forms serve a company, a trust, an
// SSAS and a SIPP. Each form is one entry of the registry below.
//
// # ONE ENGINE, ONE BANK FOR EACH FORM
//
// The engine of `client-application-questions.ts` reads one bank: its
// sections, its visibility rules and its form rules. A form gives the engine
// its bank through `form` in `ClientApplicationFormContext`. A reader that
// gives no form reads the individual form, so each reader from before SEP-293
// keeps its behaviour.
//
// # EACH FORM HAS ITS OWN VERSION LINE
//
// The firm versions each paper form apart, so a change to the trust form does
// not move the individual line. The snapshot stores the EDITION (one form at
// one version) in `ClientApplicationSubmission.formVersion`:
//
//   - null, `2.0` and `3.0` are the individual form, as before SEP-293.
//   - `<formId>@<version>` is each other form, for example `trust@1.1`.
//
// `clientApplicationEditionOf` reads the stored value, and it throws on a
// value that names no form or no version of the form.
//
// # THE ACCOUNT TYPE SELECTS THE FORM
//
// All forms open with the same first section (`service_type`, `account_types`
// and `preferred_investment_manager`), so the answers that select the form
// stay when the form changes. `clientApplicationFormFor` holds the rule. A
// form shows only when its bank exists and the caller names it as enabled:
// the feature node `dowgate.onboarding.entity-forms` and its release hold
// decide that on the server.

import {
    CLIENT_APPLICATION_BANK,
    CLIENT_APPLICATION_FORM_VERSION,
    CLIENT_APPLICATION_FORM_VERSIONS,
    clientApplicationBankVersions,
    clientApplicationSections,
    type ClientApplicationBank,
} from './client-application-questions';
import {
    type ClientApplicationPartyRole,
    clientApplicationIdentity,
    clientApplicationParties,
    clientApplicationPersonIdentity,
    type ClientApplicationIdentity,
    type ClientApplicationParty,
    type ClientApplicationPartySource,
} from './client-application-identity';
import {
    APPLICANT_CAPACITY_ID,
    COMPANY_DISCRETIONARY_ADVISORY_BANK,
    COMPANY_DISCRETIONARY_ADVISORY_FORM_VERSIONS,
    COMPANY_EXECUTION_ONLY_BANK,
    COMPANY_EXECUTION_ONLY_FORM_VERSIONS,
    COMPANY_STAKEHOLDER_COUNT,
    COMPANY_STAKEHOLDER_PREFIX,
} from './client-application-form-company';
import {
    TRUST_BANK,
    TRUST_FORM_VERSIONS,
    TRUST_PARTY_COUNT,
    TRUST_PARTY_PREFIX,
} from './client-application-form-trust';
import {
    SSAS_BANK,
    SSAS_FORM_VERSIONS,
    SSAS_MEMBER_COUNT,
    SSAS_MEMBER_PREFIX,
} from './client-application-form-ssas';
import { SIPP_BANK, SIPP_FORM_VERSIONS } from './client-application-form-sipp';
import type { ClientApplicationAnswers } from './client-application-validation';
import { FROZEN_CLIENT_APPLICATION_IDS_BY_FORM } from './client-application-frozen-ids';
import { CLIENT_APPLICATION_KEY_FACT_IDS } from './client-application-review';

/** The id of each form, in the order the firm lists the account types. */
export const CLIENT_APPLICATION_FORM_IDS = [
    'individual',
    'company_discretionary_advisory',
    'company_execution_only',
    'trust',
    'ssas',
    'sipp',
] as const;

/** The id of one form. */
export type ClientApplicationFormId = (typeof CLIENT_APPLICATION_FORM_IDS)[number];

/** The applicant type that the onboarding case of a form records. */
export type ClientApplicationApplicantType = 'Individual' | 'Trust' | 'Entity';

/** The fact find that the answers of a form seed. */
export type ClientApplicationFactFindClientType = 'individual' | 'entity';

/** One form of the client application. */
export interface ClientApplicationFormDefinition {
    readonly id: ClientApplicationFormId;
    /** The name that a staff surface prints. */
    readonly title: string;
    /** The paper source of each version, by version. */
    readonly sources: Readonly<Record<string, string>>;
    /** The version line of the form, from the oldest to the newest. */
    readonly versions: readonly string[];
    /** The version that a client fills in now. */
    readonly currentVersion: string;
    /** The bank of every version of the form. */
    readonly bank: ClientApplicationBank;
    /** What the onboarding case records. */
    readonly applicantType: ClientApplicationApplicantType;
    /** The fact find that the answers seed. */
    readonly factFindClientType: ClientApplicationFactFindClientType;
    /** The name of the account holder (a person or an entity), for the case and the Client. */
    readonly holderName: (answers: ClientApplicationAnswers) => string;
    /** The person who holds the login and gets the identity check first. */
    readonly applicant: (answers: ClientApplicationAnswers) => ClientApplicationIdentity;
    /** Each person that the identity check covers, the applicant first, with no repeat. */
    readonly parties: (answers: ClientApplicationAnswers) => readonly ClientApplicationParty[];
    /** The question ids that the staff review prints first. */
    readonly keyFactIds: readonly string[];
    /** The frozen question ids and statement ids of the form. */
    readonly frozenIds: {
        readonly questions: readonly string[];
        readonly statements: readonly string[];
    };
}

/** The individual form: General Investment, ISA and Joint (SEP-263). */
const INDIVIDUAL_FORM: ClientApplicationFormDefinition = {
    id: 'individual',
    title: 'Individual account',
    sources: {
        '2.0': 'DGW Application Form (Disc/Adv) Individual v2.0',
        '3.0': 'DGW Application Form (Disc/Adv) Individual v2.0, with the client questions of the Internal Suitability Assessment v3',
    },
    versions: CLIENT_APPLICATION_FORM_VERSIONS,
    currentVersion: CLIENT_APPLICATION_FORM_VERSION,
    bank: CLIENT_APPLICATION_BANK,
    applicantType: 'Individual',
    factFindClientType: 'individual',
    holderName: (answers) => clientApplicationIdentity(answers).fullName,
    applicant: (answers) => clientApplicationIdentity(answers),
    parties: (answers) => clientApplicationParties(answers, []),
    keyFactIds: CLIENT_APPLICATION_KEY_FACT_IDS,
    frozenIds: FROZEN_CLIENT_APPLICATION_IDS_BY_FORM.individual,
};

/**
 * The person blocks of a person group, as parties of the identity check.
 * `firstOpens` is true when block 1 opens with its own yes or no question.
 */
function groupParties(
    answers: ClientApplicationAnswers,
    group: {
        readonly prefix: string;
        readonly count: number;
        readonly role: ClientApplicationPartyRole;
        readonly firstOpens?: boolean;
    },
): ClientApplicationPartySource[] {
    const sources: ClientApplicationPartySource[] = [];
    for (let n = 1; n <= group.count; n++) {
        const prefix = `${group.prefix}_${n}`;
        // A block with an opener shows only while its opener holds Yes.
        const opens = n > 1 || group.firstOpens === true;
        if (opens && answers[`has_${prefix}`] !== 'Yes') break;
        const capacity = answers[`${prefix}_capacity`];
        sources.push({
            partyKey: prefix,
            role: group.role,
            capacity: typeof capacity === 'string' ? capacity.trim() : '',
            identity: clientApplicationPersonIdentity(answers, prefix),
        });
    }
    return sources;
}

/** The person blocks of Part 3 of a company form, as parties of the identity check. */
function companyStakeholders(answers: ClientApplicationAnswers): ClientApplicationPartySource[] {
    return groupParties(answers, {
        prefix: COMPANY_STAKEHOLDER_PREFIX,
        count: COMPANY_STAKEHOLDER_COUNT,
        role: 'stakeholder',
    });
}

/** One trimmed text answer. */
function answerText(answers: ClientApplicationAnswers, id: string): string {
    const value = answers[id];
    return typeof value === 'string' ? value.trim() : '';
}

/** The name of the company, for the case and the Client. */
function companyName(answers: ClientApplicationAnswers): string {
    const name = answers['company_name'];
    return typeof name === 'string' ? name.trim() : '';
}

/** The facts of a company application that the staff review prints first. */
const COMPANY_KEY_FACT_IDS: readonly string[] = [
    'company_name',
    'company_status',
    'company_registration_number',
    'company_country_of_incorporation',
    'company_date_of_incorporation',
    'applicant_capacity',
    'company_source_types',
    'investment_objective',
];

function companyForm(
    id: 'company_discretionary_advisory' | 'company_execution_only',
): ClientApplicationFormDefinition {
    const discretionary = id === 'company_discretionary_advisory';
    const versions: readonly string[] = discretionary
        ? COMPANY_DISCRETIONARY_ADVISORY_FORM_VERSIONS
        : COMPANY_EXECUTION_ONLY_FORM_VERSIONS;
    return {
        id,
        title: discretionary
            ? 'Corporate account (Discretionary & Advisory)'
            : 'Corporate account (Execution Only)',
        sources: discretionary
            ? { '1.1': 'DGW Corporate App Form (Disc/Adv) v1.1 0925' }
            : { '1.0': 'DGW Corporate App Form (XO) v1.0 0625' },
        versions,
        currentVersion: versions[versions.length - 1]!,
        bank: discretionary ? COMPANY_DISCRETIONARY_ADVISORY_BANK : COMPANY_EXECUTION_ONLY_BANK,
        applicantType: 'Entity',
        factFindClientType: 'entity',
        holderName: companyName,
        applicant: (answers) => clientApplicationIdentity(answers),
        parties: (answers) =>
            clientApplicationParties(answers, companyStakeholders(answers), APPLICANT_CAPACITY_ID),
        keyFactIds: COMPANY_KEY_FACT_IDS,
        frozenIds: FROZEN_CLIENT_APPLICATION_IDS_BY_FORM[id],
    };
}

/** The facts of a trust application that the staff review prints first. */
const TRUST_KEY_FACT_IDS: readonly string[] = [
    'trust_name',
    'trust_type',
    'trust_charity_number',
    'trust_country_of_establishment',
    'trust_date_established',
    'applicant_capacity',
    'source_types',
    'investment_objective',
];

/** The trust form, for a trust or a charity (SEP-293). */
const TRUST_FORM: ClientApplicationFormDefinition = {
    id: 'trust',
    title: 'Trust account',
    sources: { '1.1': 'DGW Trust App Form v1.1 0925' },
    versions: TRUST_FORM_VERSIONS,
    currentVersion: TRUST_FORM_VERSIONS[TRUST_FORM_VERSIONS.length - 1]!,
    bank: TRUST_BANK,
    applicantType: 'Trust',
    factFindClientType: 'entity',
    holderName: (answers) => answerText(answers, 'trust_name'),
    applicant: (answers) => clientApplicationIdentity(answers),
    parties: (answers) =>
        clientApplicationParties(
            answers,
            groupParties(answers, {
                prefix: TRUST_PARTY_PREFIX,
                count: TRUST_PARTY_COUNT,
                role: 'trust_party',
            }),
            APPLICANT_CAPACITY_ID,
        ),
    keyFactIds: TRUST_KEY_FACT_IDS,
    frozenIds: FROZEN_CLIENT_APPLICATION_IDS_BY_FORM.trust,
};

/** The facts of an SSAS application that the staff review prints first. */
const SSAS_KEY_FACT_IDS: readonly string[] = [
    'scheme_name',
    'scheme_hmrc_pension_number',
    'scheme_administrator_name',
    'has_member_1',
    'source_types',
    'investment_objective',
];

/** The SSAS form: the lead individual applies for the scheme (SEP-293). */
const SSAS_FORM: ClientApplicationFormDefinition = {
    id: 'ssas',
    title: 'SSAS account',
    sources: { '1.0': 'DGW SSAS App Form (Disc/Adv/XO) v1.0 0725' },
    versions: SSAS_FORM_VERSIONS,
    currentVersion: SSAS_FORM_VERSIONS[SSAS_FORM_VERSIONS.length - 1]!,
    bank: SSAS_BANK,
    // An SSAS is a trust of a pension scheme, so its case is a Trust case and
    // its fact find is the fact find of an entity.
    applicantType: 'Trust',
    factFindClientType: 'entity',
    holderName: (answers) => answerText(answers, 'scheme_name'),
    applicant: (answers) => clientApplicationIdentity(answers),
    parties: (answers) =>
        clientApplicationParties(
            answers,
            groupParties(answers, {
                prefix: SSAS_MEMBER_PREFIX,
                count: SSAS_MEMBER_COUNT,
                role: 'scheme_member',
                firstOpens: true,
            }),
        ),
    keyFactIds: SSAS_KEY_FACT_IDS,
    frozenIds: FROZEN_CLIENT_APPLICATION_IDS_BY_FORM.ssas,
};

/** The facts of a SIPP application that the staff review prints first. */
const SIPP_KEY_FACT_IDS: readonly string[] = [
    ...CLIENT_APPLICATION_KEY_FACT_IDS,
    'sipp_provider_name',
    'sipp_account_number',
];

/**
 * The SIPP form (SEP-293). A SIPP is the account of the member, so the case is
 * an Individual case and the answers seed the fact find of a person.
 */
const SIPP_FORM: ClientApplicationFormDefinition = {
    id: 'sipp',
    title: 'SIPP account',
    sources: { '1.0': 'DGW SIPP App Form (Disc/Adv/XO) v1.0 0725' },
    versions: SIPP_FORM_VERSIONS,
    currentVersion: SIPP_FORM_VERSIONS[SIPP_FORM_VERSIONS.length - 1]!,
    bank: SIPP_BANK,
    applicantType: 'Individual',
    factFindClientType: 'individual',
    holderName: (answers) => clientApplicationIdentity(answers).fullName,
    applicant: (answers) => clientApplicationIdentity(answers),
    parties: (answers) => clientApplicationParties(answers, []),
    keyFactIds: SIPP_KEY_FACT_IDS,
    frozenIds: FROZEN_CLIENT_APPLICATION_IDS_BY_FORM.sipp,
};

/**
 * Each form whose bank exists, by id.
 *
 * A form id without an entry has no bank yet. A later slice of SEP-293 adds
 * each form.
 */
export const CLIENT_APPLICATION_FORMS: Readonly<
    Partial<Record<ClientApplicationFormId, ClientApplicationFormDefinition>>
> = Object.fromEntries(
    [
        INDIVIDUAL_FORM,
        companyForm('company_discretionary_advisory'),
        companyForm('company_execution_only'),
        TRUST_FORM,
        SSAS_FORM,
        SIPP_FORM,
    ].map((form) => [form.id, form]),
);

/** The individual form. Each reader that names no form reads it. */
export const CLIENT_APPLICATION_INDIVIDUAL_FORM: ClientApplicationFormDefinition = INDIVIDUAL_FORM;

/** Each form whose bank exists, in the order of {@link CLIENT_APPLICATION_FORM_IDS}. */
export function clientApplicationForms(): readonly ClientApplicationFormDefinition[] {
    return CLIENT_APPLICATION_FORM_IDS.flatMap((id) => {
        const form = CLIENT_APPLICATION_FORMS[id];
        return form === undefined ? [] : [form];
    });
}

/** Whether a value names a form. */
export function isClientApplicationFormId(value: unknown): value is ClientApplicationFormId {
    return (CLIENT_APPLICATION_FORM_IDS as readonly unknown[]).includes(value);
}

/** The form of one id. It throws when the form has no bank yet. */
export function clientApplicationForm(
    id: ClientApplicationFormId,
): ClientApplicationFormDefinition {
    const form = CLIENT_APPLICATION_FORMS[id];
    if (form === undefined) throw new Error(`the client application has no form "${id}" yet`);
    return form;
}

// ---------------------------------------------------------------------------
// The edition key
// ---------------------------------------------------------------------------

/** One form at one version. */
export interface ClientApplicationEdition {
    readonly form: ClientApplicationFormDefinition;
    readonly version: string;
}

/**
 * The value that a snapshot stores for one edition.
 *
 * The individual form stores its version alone, as before SEP-293, so each
 * stored row keeps its meaning. Each other form stores `<formId>@<version>`.
 */
export function clientApplicationEditionKey(
    form: ClientApplicationFormDefinition,
    version: string,
): string {
    if (!form.versions.includes(version)) {
        throw new Error(`"${version}" is not a version of the form "${form.id}"`);
    }
    return form.id === 'individual' ? version : `${form.id}@${version}`;
}

/**
 * The edition of one stored snapshot.
 *
 * Null and undefined give the individual form at version 2.0, because each
 * snapshot from before SEP-293.3 used that version. A value that names no
 * form, or no version of its form, is a fault of the data, so the function
 * throws: a guess would show the answers with the wrong words.
 */
export function clientApplicationEditionOf(
    stored: string | null | undefined,
): ClientApplicationEdition {
    if (stored === null || stored === undefined) {
        return { form: INDIVIDUAL_FORM, version: CLIENT_APPLICATION_FORM_VERSIONS[0] };
    }
    const at = stored.indexOf('@');
    if (at === -1) {
        if (!INDIVIDUAL_FORM.versions.includes(stored)) {
            throw new Error(`"${stored}" is not a version of the client application form`);
        }
        return { form: INDIVIDUAL_FORM, version: stored };
    }
    const formId = stored.slice(0, at);
    const version = stored.slice(at + 1);
    if (!isClientApplicationFormId(formId) || formId === 'individual') {
        throw new Error(`"${stored}" names no form of the client application`);
    }
    const form = CLIENT_APPLICATION_FORMS[formId];
    if (form === undefined) {
        throw new Error(`"${stored}" names the form "${formId}", which has no bank yet`);
    }
    if (!form.versions.includes(version)) {
        throw new Error(`"${stored}" names no version of the form "${formId}"`);
    }
    return { form, version };
}

/** Whether the bank of a form holds the version line of the form. A test reads it. */
export function clientApplicationFormVersionsAgree(form: ClientApplicationFormDefinition): boolean {
    const line = clientApplicationBankVersions(form.bank);
    return (
        line.length === form.versions.length &&
        line.every((version, index) => version === form.versions[index]) &&
        form.versions.includes(form.currentVersion)
    );
}

// ---------------------------------------------------------------------------
// The form that the answers select (section 1.4 of the design)
// ---------------------------------------------------------------------------

/** The account type of a company. */
export const CLIENT_APPLICATION_COMPANY_OPTION = 'Company';
/** The account type of a trust or a charity. */
export const CLIENT_APPLICATION_TRUST_OPTION = 'Trust / Charity';
/** The account type of a small self-administered scheme. */
export const CLIENT_APPLICATION_SSAS_OPTION = 'SSAS';
/** The account type of a self-invested personal pension. */
export const CLIENT_APPLICATION_SIPP_OPTION = 'SIPP';
/** The service type that selects the Execution Only form of a company. */
export const CLIENT_APPLICATION_EXECUTION_ONLY_OPTION = 'Execution Only service';

/**
 * The form that the account type and the service type select, whether its
 * bank exists or not.
 *
 * Company, Trust / Charity, SSAS and SIPP each stand alone in the account
 * type list, so one value decides. A company with the Execution Only service
 * takes the Execution Only form. A company with another service, or with no
 * service yet, takes the Discretionary and Advisory form. Every other answer,
 * and no answer, takes the individual form.
 */
export function clientApplicationFormIdFor(
    answers: Readonly<Record<string, unknown>>,
): ClientApplicationFormId {
    const raw = answers['account_types'];
    const types: readonly unknown[] = Array.isArray(raw)
        ? raw
        : typeof raw === 'string'
          ? [raw]
          : [];
    if (types.includes(CLIENT_APPLICATION_COMPANY_OPTION)) {
        return answers['service_type'] === CLIENT_APPLICATION_EXECUTION_ONLY_OPTION
            ? 'company_execution_only'
            : 'company_discretionary_advisory';
    }
    if (types.includes(CLIENT_APPLICATION_TRUST_OPTION)) return 'trust';
    // A draft from before SEP-293 can hold the old label "SASS".
    if (types.includes(CLIENT_APPLICATION_SSAS_OPTION) || types.includes('SASS')) return 'ssas';
    if (types.includes(CLIENT_APPLICATION_SIPP_OPTION)) return 'sipp';
    return 'individual';
}

/**
 * The form that the answers select, from the forms that this deployment
 * shows.
 *
 * `enabledFormIds` comes from the server: the feature node
 * `dowgate.onboarding.entity-forms` and its release hold decide it. A form
 * that the deployment does not show, or that has no bank yet, gives the
 * individual form, which is the behaviour from before SEP-293.
 */
export function clientApplicationFormFor(
    answers: Readonly<Record<string, unknown>>,
    enabledFormIds: readonly ClientApplicationFormId[] = ['individual'],
): ClientApplicationFormDefinition {
    const id = clientApplicationFormIdFor(answers);
    const form = CLIENT_APPLICATION_FORMS[id];
    return form !== undefined && enabledFormIds.includes(id) ? form : INDIVIDUAL_FORM;
}

/**
 * The ids of the answers that a change from one form to another removes.
 *
 * An answer stays when the current version of the new form holds its id, for
 * example the answers of the first section, the email address, the bank
 * details and the health questions. A blank answer is not an answer.
 */
export function clientApplicationAnswersLostBySwitch(
    answers: Readonly<Record<string, unknown>>,
    from: ClientApplicationFormDefinition,
    to: ClientApplicationFormDefinition,
): readonly string[] {
    if (from.id === to.id) return [];
    const kept = new Set(
        clientApplicationSections(to.currentVersion, to.bank).flatMap((section) =>
            section.questions.map((question) => question.id),
        ),
    );
    return clientApplicationSections(from.currentVersion, from.bank)
        .flatMap((section) => section.questions)
        .filter((question) => !kept.has(question.id) && !isBlank(answers[question.id]))
        .map((question) => question.id);
}

function isBlank(value: unknown): boolean {
    if (value === null || value === undefined || value === false) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) return value.length === 0;
    return false;
}
